import React, { useRef, useState, useEffect } from 'react';
import { Group, Rect, Text, Transformer } from 'react-konva';

export interface KonvaStickyProps {
  id: string;
  text: string;
  x: number;
  y: number;
  color: string;
  fontSize: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  width: number;
  height: number;
  author?: string;
  isSelected?: boolean;
  onMove: (id: string, x: number, y: number) => void;
  onDelete: (id: string) => void;
  stageScale: number;
  stageOffset: {x:number; y:number};
  onColorRequest?: (id: string, x:number, y:number)=>void;
  onSelect?: (id: string)=>void;
  onResize?: (id:string,width:number,height:number)=>void;
  onTextChange?: (id:string,text:string)=>void;
  fontFamily: string;
  listStyle: 'none' | 'bullet';
}

const KonvaStickyNote: React.FC<KonvaStickyProps> = ({ id, text, x, y, color, fontSize, bold, italic, underline, fontFamily, listStyle, author, isSelected, onMove, onDelete, stageScale, onColorRequest, onSelect, onResize, onTextChange, width, height, stageOffset }) => {
  const groupRef = useRef<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentText, setCurrentText] = useState(text);

  // textarea overlay for editing
  useEffect(() => {
    if (isEditing) {
      const textarea = document.createElement('textarea');
      textarea.value = currentText;
      textarea.style.position = 'absolute';
      const stage = groupRef.current.getStage();
      const containerRect = stage.container().getBoundingClientRect();
      const abs = groupRef.current.getAbsolutePosition();
      const pad = 8 * stageScale;
      textarea.style.top = `${containerRect.top + abs.y + pad}px`;
      textarea.style.left = `${containerRect.left + abs.x + pad}px`;
      textarea.style.width = `${width*stageScale - 2*pad}px`;
      textarea.style.height = `${height*stageScale - 2*pad}px`;
      textarea.style.fontSize = `${fontSize/ stageScale}px`;
      textarea.style.background='transparent';
      textarea.style.border='none';
      textarea.style.color='#1f2937';
      textarea.style.padding = '0';
      textarea.style.boxSizing='border-box';
      textarea.style.outline='none';
      textarea.style.resize='none';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.oninput = () => {
        setCurrentText(textarea.value);
        onTextChange && onTextChange(id, textarea.value);
        // auto resize height
        const contentH = textarea.scrollHeight; // in px
        const pad = 8 * stageScale;
        const desiredH = contentH / stageScale + pad*2/stageScale;
        if(desiredH > height || desiredH < height - 24){
          onResize && onResize(id, width, desiredH);
          const rNode = groupRef.current.findOne('Rect');
          if(rNode){ rNode.height(desiredH);}
        }
      };
      textarea.onblur = () => {
        setIsEditing(false);
        setCurrentText(textarea.value);
        onTextChange && onTextChange(id, textarea.value);
        document.body.removeChild(textarea);
      };
    }
  }, [isEditing]);

  useEffect(()=>{ if(currentText==='' && isSelected===true){ setIsEditing(true);} },[currentText,isSelected]);

  const fontStyle = `${bold ? 'bold' : ''} ${italic ? 'italic' : ''}`.trim();
  const textDecoration = underline ? 'underline' : undefined;

  const trRef = useRef<any>(null);

  useEffect(()=>{
    if(isSelected && trRef.current){
      trRef.current.nodes([groupRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  },[isSelected]);

  const handleTransformEnd=()=>{
    const node = groupRef.current;
    const rect = node.findOne('Rect');
    const newW=Math.max(60, rect.width()*node.scaleX());
    const newH=Math.max(40, rect.height()*node.scaleY());
    // update internal rect and text instantly
    rect.width(newW);
    rect.height(newH);
    const texts = node.find('Text');
    texts.forEach((t:any,index:number)=>{
      if(index===0){ // content text
        t.width(newW);
        t.height(newH-20);
      } else { // author label
        t.y(newH-18);
        t.width(newW);
      }
    });
    node.scale({x:1,y:1});
    onResize && onResize(id,newW,newH);
  };

  // compute displayText line
  const displayText = listStyle==='bullet' ? (currentText.startsWith('• ') ? currentText : `• ${currentText}`) : currentText;

  return (
    <>
    <Group
      x={x}
      y={y}
      draggable
      onDragEnd={(e) => onMove(id, e.target.x(), e.target.y())}
      ref={groupRef}
      onContextMenu={(e)=>{ e.evt.preventDefault(); if(onColorRequest){ onColorRequest(id, e.evt.clientX, e.evt.clientY);} }}
      onDblClick={() => setIsEditing(true)}
      onClick={()=>{ onSelect && onSelect(id); if(!isEditing) setIsEditing(true);} }
      onTransformEnd={handleTransformEnd}
    >
      <Rect width={width} height={height} fill={color} cornerRadius={6} shadowBlur={4} stroke={isSelected ? '#3b82f6' : undefined} strokeWidth={isSelected?2:0}/>
      <Text
        visible={!isEditing}
        text={displayText}
        padding={8}
        width={width}
        height={height-20}
        fontSize={fontSize}
        fill="#1f2937"
        fontStyle={fontStyle}
        textDecoration={textDecoration}
        fontFamily={fontFamily}
      />
      {author && (
        <Text
          text={author}
          y={height-18}
          width={width}
          align="right"
          fontSize={10}
          fill="#6b7280"
          padding={4}
        />
      )}
    </Group>
    {isSelected && <Transformer ref={trRef} boundBoxFunc={(o,n)=>{ if(n.width<60||n.height<40) return o; return n; }} />}
    </>
  );
};

export default KonvaStickyNote; 